import React, { ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox, Form, FormInstance, InputNumber, Select } from 'antd';

import EventsSelect from '@src/emp-3.0/components/EventsSelect';
import CustomFormItem from '@src/emp-3.0/components/Metrics/IndividualMetric/MetricBuilder/CustomFormItem';
import { CalculableMetricSelect } from './CalculableMetricSelect';
import {
  EGetMetricWay,
  getMetricOptions,
  MetricRole,
  RatioMetricInfo,
  useGetEventToEdit,
} from './service';
import { EventConfig } from './EventConfig';
import { MetricDataScope } from '@src/emp-3.0/types';
import useFieldRequiredStatus from '@src/pages/workflow/hooks/useFieldRequiredStatus';
import { get } from 'lodash';
import { useGetCalculableMetrics, useGetMetricList } from '@src/emp-3.0/api/queryHooks';
import { useNavigate } from 'react-router-dom';

interface GetMetricOptions {
  form: FormInstance;
  metricRole: MetricRole;
  isView?: boolean;
  /** which source the metric or event belongs to */
  dataScope?: MetricDataScope[];
  metricInfo?: RatioMetricInfo;
}
export const GetMetric: React.FC<GetMetricOptions> = ({
  form,
  metricRole,
  dataScope,
  isView,
  metricInfo,
}) => {
  const metricId = Form.useWatch(['definition', metricRole, 'used_metric_id'], form);
  const isNumeratorFilterEnabled = Form.useWatch(
    ['definition', metricRole, 'is_numerator_filter_enabled'],
    form,
  );
  const fixedNumberValue = Form.useWatch(['definition', metricRole, 'fixed_number'], form);
  const { data: calculableMetrics = [] } = useGetCalculableMetrics();
  const { data: userDefinedMetrics = [] } = useGetMetricList();
  const { eventToEdit, setEventToEdit } = useGetEventToEdit(metricRole);
  const navigate = useNavigate();

  const metricInUse = useMemo(() => {
    if (!metricId) {
      return undefined;
    }
    const target = calculableMetrics.find((it) => it.id === metricId);
    return target;
  }, [metricId, calculableMetrics]);

  const [getMetricWay, setGetMetricWay] = useState<EGetMetricWay>(EGetMetricWay.existing_metric);
  useEffect(() => {
    const info = get(metricInfo, ['definition', metricRole]);
    if (info === undefined) {
      return;
    }
    const { event, fixed_number } = info;
    if (event !== undefined) {
      setGetMetricWay(EGetMetricWay.build_from_event);
    } else if (fixed_number !== undefined) {
      setGetMetricWay(EGetMetricWay.fixed_number);
    } else {
      setGetMetricWay(EGetMetricWay.existing_metric);
    }
  }, [metricInfo, metricRole]);
  useEffect(() => {
    if (!eventToEdit) {
      return;
    }
    if (getMetricWay !== EGetMetricWay.build_from_event) {
      setGetMetricWay(EGetMetricWay.build_from_event);
    }
  }, [eventToEdit, getMetricWay]);

  const { status: metricSelectStatus, enableRequired } = useFieldRequiredStatus([
    'definition',
    metricRole,
    'used_metric_id',
  ]);
  const { status: eventSelectStatus } = useFieldRequiredStatus(['definition', metricRole, 'event']);
  const { status: fixedNumberStatus } = useFieldRequiredStatus([
    'definition',
    metricRole,
    'fixed_number',
  ]);
  const renderMetricConfig = useCallback(() => {
    const needConfigMetric = metricRole === 'denominator' && metricInUse?.is_predefined;
    const isUserDefined = userDefinedMetrics.find((it) => it.id === Number(metricInUse?.id));
    return (
      <div
        style={{
          marginLeft: '26px',
        }}
      >
        {needConfigMetric ? (
          <Form.Item
            name={['definition', metricRole, 'is_numerator_filter_enabled']}
            valuePropName="checked"
            style={isView ? { display: 'none' } : {}}
          >
            <Checkbox>{`Only include ${metricInUse.name} who qualify for the numerator`}</Checkbox>
          </Form.Item>
        ) : null}
        {isView && metricInUse ? (
          <div>
            {isUserDefined ? (
              <a
                onClick={(evt) => {
                  evt.preventDefault();
                  navigate(`/metric-builder/metric/${metricInUse.id}`);
                }}
              >
                {metricInUse.name}
              </a>
            ) : (
              metricInUse.name
            )}
            {needConfigMetric && isNumeratorFilterEnabled
              ? `, Only include ${metricInUse.name} who qualify for the numerator`
              : ''}
          </div>
        ) : null}
      </div>
    );
  }, [isNumeratorFilterEnabled, isView, metricRole, navigate, metricInUse, userDefinedMetrics]);
  const options = useMemo(() => {
    const tempOptions = [...getMetricOptions];
    if (metricRole === 'denominator') {
      tempOptions.push({
        label: 'Fixed Number',
        value: EGetMetricWay.fixed_number,
      });
    }
    return tempOptions;
  }, [metricRole]);
  return (
    <>
      <div
        style={{
          display: isView ? 'none' : 'flex',
          alignItems: 'flex-start',
          gap: '16px',
          marginLeft: '26px',
        }}
      >
        <Select
          size="middle"
          options={options}
          value={getMetricWay}
          onChange={(value) => {
            setGetMetricWay(value);
          }}
          style={{
            width: '280px',
          }}
          placeholder="Select metric type"
        />
        <CustomFormItem
          name={[
            'definition',
            metricRole,
            getMetricWay === EGetMetricWay.existing_metric
              ? 'used_metric_id'
              : getMetricWay === EGetMetricWay.build_from_event
              ? 'event'
              : 'fixed_number',
          ]}
          style={{ flex: 1 }}
          labelAlign="left"
          validateStatus={
            getMetricWay === EGetMetricWay.existing_metric
              ? metricSelectStatus
              : getMetricWay === EGetMetricWay.build_from_event
              ? eventSelectStatus
              : fixedNumberStatus
          }
          rules={
            getMetricWay === EGetMetricWay.fixed_number
              ? ([
                  enableRequired && { required: true, message: 'Fixed number is required' },
                  {
                    validator(_, value) {
                      if (value === undefined || value === null || value === '') {
                        return Promise.reject('Fixed number is required');
                      }
                      if (typeof value !== 'number' || value < 1 || value > 1000000) {
                        return Promise.reject('Number must be between 1 and 1,000,000');
                      }
                      return Promise.resolve();
                    },
                  },
                ].filter(Boolean) as ComponentProps<typeof Form.Item>['rules'])
              : ([enableRequired && { required: true, message: 'Event is required' }].filter(
                  Boolean,
                ) as ComponentProps<typeof Form.Item>['rules'])
          }
        >
          {getMetricWay === EGetMetricWay.build_from_event ? (
            <EventsSelect
              dataScope={dataScope}
              onChange={() => {
                setEventToEdit(null);
              }}
              checkValue={true}
            ></EventsSelect>
          ) : getMetricWay === EGetMetricWay.existing_metric ? (
            <CalculableMetricSelect
              value={metricId}
              dataScope={dataScope}
              metricRole={metricRole}
            ></CalculableMetricSelect>
          ) : (
            <InputNumber
              placeholder="Enter fixed number"
              style={{ width: '300px' }}
              precision={0}
              onKeyPress={(e) => {
                if (!/[0-9]/.test(e.key)) {
                  e.preventDefault();
                }
              }}
            />
          )}
        </CustomFormItem>
      </div>
      {getMetricWay === EGetMetricWay.build_from_event ? (
        <EventConfig form={form} metricRole={metricRole} isView={isView}></EventConfig>
      ) : getMetricWay === EGetMetricWay.existing_metric ? (
        renderMetricConfig()
      ) : (
        isView && <div style={{ marginLeft: '26px' }}>{fixedNumberValue || 'No value set'}</div>
      )}
    </>
  );
};
